import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import { VStack, Box, Button, Link } from "@chakra-ui/react";
import { androidAppDownloadLink, iosAppDownloadLink } from "utils/constants";
import { reclaimMobileAppUrIScheme, reclaimProtocolMobileAppUrIScheme } from "utils/constants";
import { userAgentIsAndroid, userAgentIsIOS, userAgentIsMobile } from "utils/device";
import { GetAppModal } from "components/OpenInAppDialog";
import { Image, Text } from "@chakra-ui/react";

// Chromium legacy browsers (< Chromium 91.0) do not handle external protocols: https://github.com/blueboxd/chromium-legacy/issues/18

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const Prove: React.FC = () => {
  const [isGetAppModalOpen, setIsGetAppModalOpen] = useState(false);
  const query = useQuery();

  const getStoreLink = () => {
    if (userAgentIsAndroid) {
      return androidAppDownloadLink;
    } else {
      return iosAppDownloadLink;
    }
  };

  const onGetAppClick = () => {
    const storeLink = getStoreLink();
    window.open(storeLink, "_blank", "noopener,noreferrer");
    // we are not sure what platform we are on, so just let user choose anyway as fallback if the previous auto-open didn't work...
    setIsGetAppModalOpen(true);
  };

  const openLinkInMobileApp = () => {
    const template = query.get("template");

    if (!template) return;

    const url = encodeURIComponent(template);
    const encodedUrl = url.replace(/[()]/g, (match) => {
      return "%" + match.charCodeAt(0).toString(16).toUpperCase();
    });
    const appUrl = `${reclaimProtocolMobileAppUrIScheme}://requestedProofs/${encodedUrl}`;

    window.open(appUrl, "_self", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!userAgentIsIOS) return;

    const template = query.get("template");
    if (!template) return;
    const url = encodeURIComponent(template);
    const encodedUrl = url.replace(/[()]/g, (match) => {
      return "%" + match.charCodeAt(0).toString(16).toUpperCase();
    });
    window.open(
      `https://appclip.apple.com/id?p=org.reactjs.native.example.Reclaim.Clip&template=${encodedUrl}`,
      "_self",
      "noopener,noreferrer"
    );
  }, [query]);

  const getUrl = () => {
    const template = query.get("template");
    if (!template) return "https://share.reclaimprotocol.org/prove";
    const url = encodeURIComponent(template);
    const encodedUrl = url.replace(/[()]/g, (match) => {
      return "%" + match.charCodeAt(0).toString(16).toUpperCase();
    });

    return `https://share.reclaimprotocol.org/prove?template=${encodedUrl}`;
  };

  return (
    <>
      <meta
        name="apple-itunes-app"
        content="app-id=6475267895, app-clip-bundle-id=69T6V5YN8P.org.reactjs.native.example.Reclaim.Clip, app-clip-display=card"
      />
      <VStack alignContent="center" marginTop="50%" paddingLeft="5%" mx="auto" justify={"center"}>
        <Box display="flex" flexDirection="column" alignItems="center" gap="4">
          <Image src="/logo200.png" alt="Reclaim logo" />
          <Text fontSize={"17px"} color="#332FED">
            Secured by Reclaim Protocol
          </Text>
        </Box>
        <VStack paddingTop="50%">
          <Text fontSize={"19px"} fontWeight="700">
            You will be redirected to create a proof, in just a moment.
          </Text>
          <Text fontSize={"19px"} fontWeight="400">
            If you are not redirected automatically,{" "}
            <Link
              href="#"
              onClick={() => window.open(getUrl(), "_blank")}
              style={{ color: "#332FED" }}
            >
              tap here
            </Link>{" "}
            to start manually.
          </Text>
          <Text fontSize={"19px"} fontWeight="400">
            If it doesn't work go through this troubleshooting list:
          </Text>
          {userAgentIsAndroid && (
            <>
              <Text fontSize={"19px"} fontWeight="400">
                {"- Check that pop-ups are allowed in Chrome."}
              </Text>
              <Text fontSize={"19px"} fontWeight="400">
                - If you're using Firefox, ensure that Open links in apps is not set to Never
                (should be set to Always or Ask before)
              </Text>
              <Text fontSize={"19px"} fontWeight="400">
                {
                  "- Make sure InstantApps are enabled: Google Play app -> Settings ->  General ->  Google Play Instant -> Turn Upgrade web links on. "
                }
                <Text fontSize={"16px"} fontWeight="300">
                  This step is not common since the default behaviour is to enable InstantApps, but
                  it's important to be aware of it.
                </Text>
              </Text>
              <Text fontSize={"19px"} fontWeight="400">
                {
                  "- Uninstall InstantApp if it's installed (search for Reclaim Protocol in Settings -> Apps)"
                }
              </Text>
            </>
          )}
          {userAgentIsIOS && (
            <>
              <Text fontSize={"19px"} fontWeight="400">
                - Ensure that users are using Safari, as AppClips cannot be triggered from other
                browsers
              </Text>
              <Text fontSize={"19px"} fontWeight="400">
                - Remove the appclip from background (we are working on a fix for this)
              </Text>
              <Text fontSize={"19px"} fontWeight="400">
                - Uninstall Reclaim Clip from settings and try again
              </Text>
            </>
          )}
          <Text fontSize={"19px"} fontWeight="400">
            If that doesn't help, try installing the{" "}
            <Link href="#" onClick={onGetAppClick} style={{ color: "#332FED" }}>
              Reclaim Protocol App.
            </Link>
          </Text>
        </VStack>
        <Box paddingTop="20%">
          <Button
            variant="unstyled"
            backgroundColor="#332FED"
            width="326px"
            height="40px"
            borderRadius="12px"
            color="white"
            fontSize="13px"
            onClick={openLinkInMobileApp}
          >
            Open app
          </Button>
        </Box>

        <GetAppModal isOpen={isGetAppModalOpen} onClose={() => setIsGetAppModalOpen(false)} />
      </VStack>
    </>
  );
};

export default Prove;
